"use client";

import React, { useState, useEffect, useMemo } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const translations = {
  badge: {
    vi: "Hệ sinh thái ứng dụng thực tế",
    en: "Applied Ecosystem in Action"
  },
  heading: {
    vi: "Cách ba trụ cột kết nối trong các mô hình thực tế",
    en: "How our three pillars connect in real builds"
  },
  subtitle: {
    vi: "Khám phá cách học viên kết hợp kỹ thuật chế tạo robot, tư duy lập trình và nghiên cứu khoa học vào các sản phẩm hoàn chỉnh.",
    en: "See how students combine robotics engineering, coding logic, and scientific inquiry in working prototypes."
  },
  error: {
    vi: "Hiện chưa thể hiển thị các dự án của học viên lúc này.",
    en: "Unable to display student projects at this moment."
  },
  tryAgain: {
    vi: "Thử lại",
    en: "Try again"
  },
  empty: {
    vi: "Hiện chưa có dự án học viên nào. Vui lòng quay lại sau.",
    en: "No student projects available right now. Check back soon."
  },
  fallbackTitle: {
    vi: "Dự án học viên",
    en: "Student Project"
  },
  explore: {
    vi: "Khám phá dự án",
    en: "Explore project"
  }
};
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png-wvc-sizes",
  alt: "ROBOTO Mascot"
};
function ProjectCardConsumer({
  lang
}: {
  lang: "vi" | "en";
}) {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {Array.from({
        length: 3
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm min-w-0">
            <Skeleton className="h-52 w-full rounded-none" />
            <div className="p-6 space-y-3 min-w-0">
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-9 w-28 mt-4" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-card-foreground shadow-sm">
        <p className="text-muted-foreground text-sm">
          {translations.error[lang]}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
          {translations.tryAgain[lang]}
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-card-foreground shadow-sm">
        <p className="text-muted-foreground text-sm">
          {translations.empty[lang]}
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {posts.map((post, i) => {
      const title = post.customFields?.project_name || (post as {
        acf?: {
          project_name?: string;
        };
      }).acf?.project_name || post.title || translations.fallbackTitle[lang];
      const description = post.customFields?.short_description || (post as {
        acf?: {
          short_description?: string;
        };
      }).acf?.short_description || post.excerpt || "";
      const fieldDomain = post.customFields?.field_domain || (post as {
        acf?: {
          field_domain?: string;
        };
      }).acf?.field_domain || "Robotics";
      const rawImageUrl = post.customFields?.image || (post as {
        acf?: {
          image?: string;
        };
      }).acf?.image || post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/example_image_name.png";
      const projectImage = {
        src: rawImageUrl,
        srcset: "",
        sizes: ""
      };
      const projectLink = post.link || "#";
      return <article key={post.id} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-secondary hover:shadow-md min-w-0">
            <div className="min-w-0">
              {/* Media Container */}
              <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                {projectImage.src && <img src={projectImage.src} data-wvc-srcset={projectImage.srcset} data-wvc-sizes={projectImage.sizes} alt={title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />}
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="rounded-full px-3 py-1 font-sans text-xs font-semibold tracking-wide uppercase">
                    {fieldDomain}
                  </Badge>
                </div>
              </div>

              {/* Text Content */}
              <div className="p-6 min-w-0">
                <h3 className="font-serif text-xl font-bold tracking-tight text-foreground transition-colors group-hover:text-primary">
                  {title}
                </h3>
                {description && <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground line-clamp-3">
                    {description}
                  </p>}
              </div>
            </div>

            {/* Action Footer */}
            <div className="px-6 pb-6 pt-2">
              <Button asChild variant="ghost" size="sm" className="w-full justify-between rounded-lg border border-border/60 bg-muted/30 px-3.5 py-2 font-sans text-xs font-semibold text-foreground transition-[color,background-color,border-color] group-hover:border-secondary group-hover:bg-secondary/10 group-hover:text-foreground">
                <Link to={projectLink}>
                  <span>{translations.explore[lang]}</span>
                  <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1 text-secondary" />
                </Link>
              </Button>
            </div>
          </article>;
    })}
    </div>;
}
export default function EcosystemProjects() {
  const [lang, setLang] = useState<"vi" | "en">(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("roboto_lang");
      return stored === "en" ? "en" : "vi";
    }
    return "vi";
  });
  useEffect(() => {
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent<"vi" | "en">;
      if (customEvent.detail && (customEvent.detail === "vi" || customEvent.detail === "en")) {
        setLang(customEvent.detail);
      } else {
        const stored = localStorage.getItem("roboto_lang");
        setLang(stored === "en" ? "en" : "vi");
      }
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "roboto_lang") {
        setLang(e.newValue === "en" ? "en" : "vi");
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);
  const wpQuery = useMemo(() => {
    const wp_query_params = {
      post_type: "project",
      per_page: 3,
      status: ["publish"]
    };
    return new WP_Query(wp_query_params);
  }, []);
  return <section id="ecosystemprojects" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {/* Decorative Static Floating Mascot */}
      <div aria-hidden="true" className="hidden md:block absolute top-8 right-6 lg:right-12 xl:right-16 w-20 h-20 lg:w-24 lg:h-24 pointer-events-none select-none z-0">
        <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain pointer-events-none select-none drop-shadow-sm" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Spec Rail */}
        <div role="presentation" className="h-px w-full bg-secondary/70 mb-12 md:mb-16" />

        {/* Narrative Framing Header */}
        <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/10 px-3 py-1 mb-4">
            <Sparkles className="size-3.5 text-secondary" />
            <span className="font-sans text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              {translations.badge[lang]}
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            {translations.heading[lang]}
          </h2>

          <p className="mt-4 font-sans text-base text-muted-foreground leading-relaxed">
            {translations.subtitle[lang]}
          </p>
        </div>

        {/* Dynamic Project Cards Provider Container */}
        <PostsProvider wp_query={wpQuery} className="w-full">
          <ProjectCardConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}